import { Icon } from "@atoms";
import { useModal } from "@hooks";
import { AvatarStack, GroupAvatar } from "@molecules";
import { LocationSection } from "@lib/store/src/data/timeslots/viewmodel-helpers/constructConnectionsOverview";
import { ModalPage } from "../../ModalPage";
import { ConnectionOverview } from "./ConnectionOverview";
import { useTranslation } from "react-i18next";
import { useMemo, useState } from "react";
import { GroupAndReservations } from "@lib/store";

const maxGroupsToShow = 3;

export const ConnectionOverviewGroupSection = ({
  dateKey,
  locationSection,
}: {
  dateKey: string;
  locationSection: LocationSection;
}) => {
  const { t } = useTranslation();

  const [expanded, setExpanded] = useState(false);

  const [initialGroups, otherGroups] = useMemo(
    () => [locationSection.groups.slice(0, maxGroupsToShow), locationSection.groups.slice(maxGroupsToShow)],
    [locationSection.groups],
  );

  const totalUniqueGroupUsers = locationSection.groups.reduce((acc, { reservations }) => {
    const uniqueUsers = new Set(reservations.map(r => r.owner.id));
    return acc + uniqueUsers.size;
  }, 0);

  return (
    <div className="">
      <p className="text-mapiq-black-500">
        {totalUniqueGroupUsers} {t("groups:GroupMembersLabel")}
      </p>
      <div className="divide-y">
        {initialGroups.map(({ group, reservations }) => (
          <GroupAndReservationItem
            key={group.id}
            group={group}
            reservations={reservations}
            dateKey={dateKey}
          />
        ))}
        {expanded &&
          otherGroups.map(({ group, reservations }) => (
            <GroupAndReservationItem
              key={group.id}
              group={group}
              reservations={reservations}
              dateKey={dateKey}
            />
          ))}
      </div>
      {otherGroups.length > 0 && (
        <button
          onClick={() => setExpanded(!expanded)}
          className="text-collaborative-blue-500* font-semibold">
          {expanded ? t("translation:ShowLess") : t("translation:ShowMore")}
        </button>
      )}
    </div>
  );
};

const GroupAndReservationItem = ({ group, reservations, dateKey }: { dateKey: string } & GroupAndReservations) => {
  const { t } = useTranslation();

  const uniqueUsers = Array.from(new Set(reservations.map(r => r.owner.id))).map(
    id => reservations.find(r => r.owner.id === id)!.owner,
  );
  const { modalPages, addPages, openModal, closeModal, removePages } = useModal();

  const acceptedMembers = group.members.filter(member => member.status === "Accepted");
  return (
    <button
      key={group.id}
      onClick={() => {
        addPages([
          <ModalPage
            title={group.name}
            onBack={modalPages.length >= 1 ? () => removePages(-1) : undefined}
            onClose={closeModal}>
            <div className="py-4">
              <ConnectionOverview
                hideTitle
                dateKey={dateKey}
                buildingId={reservations.at(0)?.workspace.buildingId}
                groupId={group.id}
              />
            </div>
          </ModalPage>,
        ]);
        openModal();
      }}
      className={`grid grid-cols-[auto_1fr_auto_auto] gap-x-4 items-center w-full py-4 text-left hover:bg-mapiq-black-50 transition-colors`}>
      <GroupAvatar
        size="medium"
        strokeWidth={3}
        group={{ ...group, workdayCount: uniqueUsers.length, memberCount: acceptedMembers.length }}
        className="shadow-mapiq-small shrink-0"
      />

      <div className="truncate">
        <p className="font-semibold text-mapiq-black-800* truncate">{group.name}</p>

        <p className=" text-mapiq-black-500 truncate">
          {uniqueUsers.length}/{acceptedMembers.length} {t("groups:Members")}
        </p>
      </div>

      <AvatarStack
        users={uniqueUsers}
        max={3}
        overlap
        date={dateKey}
      />
      <Icon
        icon="caretRight"
        className="ml-auto self-center size-5"
      />
    </button>
  );
};
